import * as yup from "yup";
import {
  DaysOfWeek,
  EventType,
  PrepaymentProvider,
  RecurringPeriodType,
} from "../../../../../common/enums";
import { CUSTOM_FORM_LINK_OPTION_ID } from "../../../../../common/preReservationForms/constants";
import {
  fieldAtLeastOneRequired,
  fieldMaxLength,
  fieldMaxNumber,
  fieldMustBePositive,
  requiredField,
} from "../../../../../common/infrastructure/helpers/yupHelper";

export const eventWizardFormSchema = yup
  .object({
    id: yup.number().defined().required(),
    studioId: yup.number().defined().required(),
    employeeId: yup.number().nullable(),

    individualUserId: yup.number().when("type", {
      is: EventType.Individual,
      then: (schema) =>
        schema
          .positive(requiredField("Потребител"))
          .required(requiredField("Потребител")),
      otherwise: (schema) => schema.notRequired(),
    }),

    name: yup
      .string()
      .nullable()
      .defined()
      .required(requiredField("Име"))
      .max(200, fieldMaxLength("Име", 200)),
    durationInMinutes: yup
      .number()
      .defined()
      .required(requiredField("Продължителност"))
      .positive(fieldMustBePositive("Продължителност")),
    date: yup
      .date()
      .nullable()
      .defined()
      .typeError("Моля изберете валидни дата и час.")
      .required(requiredField("Дата и час на започване")),
    planTypeIds: yup
      .array()
      .defined()
      .of(yup.number().required())
      .nonNullable(),
    price: yup
      .number()
      .defined()
      .required(requiredField("Цена"))
      .max(200, fieldMaxNumber("Цена", 200))
      .min(1),
    isMultisportAvailable: yup.boolean().nullable(),

    hasCustomMultisportPrice: yup.boolean().defined().required(),

    multisportCustomPrice: yup
      .number()
      .nullable()
      .when("hasCustomMultisportPrice", {
        is: true,
        then: (schema) =>
          schema
            .required(requiredField("Печалба от Multisport посещение"))
            .positive(fieldMustBePositive("Печалба от Multisport посещение")),
        otherwise: (schema) => schema.notRequired(),
      }),

    availableSpaces: yup.number().nullable(),

    isRecurring: yup.boolean().defined().required(),

    willTakePlace: yup.boolean().defined().required(),

    showInUserPortal: yup.boolean().defined().required(),

    allowOnlineReservations: yup.boolean().defined().required(),

    // Remote class: Planny creates a Google Meet link from the owner's Google account.
    isRemote: yup.boolean().defined().required(),

    forceEmailNotifications: yup.boolean().defined().required(),

    applyChangeToAllFutureEvents: yup.boolean().defined().required(),

    requirePrepayment: yup.boolean().defined().required(),

    prepaymentProvider: yup
      .mixed<PrepaymentProvider>()
      .nullable()
      .when("requirePrepayment", {
        is: true,
        then: (schema) => schema.required(requiredField("Начин на плащане")),
        otherwise: (schema) => schema.notRequired(),
      }),

    allowSubscriptionPrepayment: yup.boolean().defined().required(),

    prepaymentLink: yup
      .string()
      .nullable()
      .when(["requirePrepayment", "prepaymentProvider"], {
        is: (requirePrepayment: boolean, prepaymentProvider: PrepaymentProvider | null) =>
          requirePrepayment && prepaymentProvider === PrepaymentProvider.Link,
        then: (schema) =>
          schema
            .required(requiredField("Линк за плащане"))
            .max(500, fieldMaxLength("Линк за плащане", 500))
            .test(
              "starts-with-https",
              "Линкът трябва да започва с https:// и да съдържа адрес",
              (value) => {
                if (!value) return true;
                return value.startsWith("https://") && value.length > 8;
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    requireFormCompletion: yup.boolean().defined().required(),

    preReservationFormId: yup.number().nullable().defined(),

    allowMusicSuggestions: yup.boolean().defined().required(),

    formCompletionLink: yup
      .string()
      .nullable()
      .when(["requireFormCompletion", "preReservationFormId"], {
        is: (
          requireFormCompletion: boolean,
          preReservationFormId: number | null | undefined,
        ) =>
          requireFormCompletion === true &&
          (preReservationFormId === null ||
            preReservationFormId === undefined ||
            preReservationFormId === CUSTOM_FORM_LINK_OPTION_ID),
        then: (schema) =>
          schema
            .required(requiredField("Линк към формуляра"))
            .max(500, fieldMaxLength("Линк към формуляра", 500))
            .test(
              "starts-with-https",
              "Линкът трябва да започва с https://",
              (value) => {
                if (!value) return true;
                return value.startsWith("https://");
              },
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    recurringDays: yup
      .array()
      .of(
        yup
          .mixed<DaysOfWeek>()
          .oneOf(Object.values(DaysOfWeek) as DaysOfWeek[])
          .required(requiredField("Дни от седмицата")),
      )
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema
            .min(1, fieldAtLeastOneRequired("ден от седмицата"))
            .required(requiredField("Дни от седмицата")),
        otherwise: (schema) => schema.notRequired(),
      }),

    recurringPeriod: yup
      .number()
      .positive(fieldMustBePositive("Период"))
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema.required(requiredField("Период")).when("recurringPeriodType", {
            is: RecurringPeriodType.Months,
            then: (schema) =>
              schema.max(3, "Периодът не може да бъде повече от 3 месеца"),
            otherwise: (schema) =>
              schema.max(12, "Периодът не може да бъде повече от 12 седмици"),
          }),
        otherwise: (schema) => schema.notRequired(),
      }),

    recurringPeriodType: yup
      .mixed<RecurringPeriodType>()
      .oneOf(Object.values(RecurringPeriodType) as number[])
      .when("isRecurring", {
        is: true,
        then: (schema) => schema.required(requiredField("Тип на период")),
        otherwise: (schema) => schema.notRequired(),
      }),

    recurringInterval: yup
      .number()
      .positive(fieldMustBePositive("Интервал"))
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema
            .required(requiredField("Интервал"))
            .max(24, "Интервалът не може да бъде повече от 24"),
        otherwise: (schema) => schema.notRequired(),
      }),

    recurringIntervalType: yup
      .mixed<RecurringPeriodType>()
      .oneOf(Object.values(RecurringPeriodType) as number[])
      .when("isRecurring", {
        is: true,
        then: (schema) => schema.required(requiredField("Тип интервал")),
        otherwise: (schema) => schema.notRequired(),
      }),

    hasRecurringVisitors: yup.boolean().nullable(),
    recurringVisitorsIds: yup.array().nullable(),
    hallId: yup.number().positive().min(0).nullable(),
    eventTypeCategoryId: yup.number().positive().min(0).nullable(),
    description: yup.string().nullable(),
    type: yup
      .mixed<EventType>()
      .defined()
      .oneOf(Object.values(EventType) as EventType[])
      .required(requiredField("Тип на класа")),
  })
  .required();

export type IClassWizardFormModel = yup.InferType<typeof eventWizardFormSchema>;

export const eventWizardFormDefaultValues: IClassWizardFormModel = {
  id: 0,
  name: "",
  studioId: 0,
  employeeId: null,
  individualUserId: 0,
  durationInMinutes: 60,
  applyChangeToAllFutureEvents: false,
  requirePrepayment: false,
  prepaymentProvider: null,
  prepaymentLink: null,
  allowSubscriptionPrepayment: false,
  requireFormCompletion: false,
  preReservationFormId: null,
  allowMusicSuggestions: false,
  formCompletionLink: null,
  date: new Date(),
  planTypeIds: [0],
  price: 0,
  isMultisportAvailable: false,
  hasCustomMultisportPrice: false,
  multisportCustomPrice: null,
  availableSpaces: 0,
  isRecurring: false,
  willTakePlace: true,
  showInUserPortal: true,
  allowOnlineReservations: true,
  isRemote: false,
  forceEmailNotifications: false,
  recurringDays: [DaysOfWeek.Monday],
  recurringPeriod: 1,
  recurringPeriodType: RecurringPeriodType.Months,
  recurringInterval: 1,
  recurringIntervalType: RecurringPeriodType.Weeks,
  hasRecurringVisitors: false,
  recurringVisitorsIds: [],
  description: "",
  hallId: null,
  eventTypeCategoryId: null,
  type: EventType.Group,
};

// UI sentinels stored inside planTypeIds for the two mutually-exclusive special options
// in the "Разрешени абонаментни карти" multiselect. Both are stripped before submit and
// translated into a SubscriptionPaymentMode.
export const ALL_PLAN_TYPES_SENTINEL = 0; // "Всички" — every plan type accepted
export const NONE_PLAN_TYPES_SENTINEL = -1; // "Никакви" — no subscription plan may pay
